import { NavLink, Link, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useState, useEffect } from "react";
import { useMember } from "@/contexts/MemberAuthContext";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";

export default function Header() {
  const { t, locale } = useI18n();
  const isFr = locale === "fr";
  const [open, setOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const location = useLocation();
  const { member, logout } = useMember();
  const isPresence = location.pathname.startsWith("/presence");

  // Standard site links (Accueil retiré — le logo en haut à gauche ramène à l'accueil)
  const volunteerPath = locale === "fr" ? "/devenir-benevole" : "/become-a-volunteer";
  const accessLabel = {
    fr: "Accès et horaires des marées",
    en: "Access & tide times",
    it: "Accesso e orari delle maree",
    es: "Acceso y horarios de las mareas",
    de: "Anreise & Gezeitenzeiten",
    pt: "Acesso e horários das marés",
    sv: "Tillfart & tidvatten",
    nb: "Adkomst & tidevann",
  }[locale] || "Accès et horaires des marées";
  const hospitalityLabel = { fr: "Hospitalité", en: "Hospitality", it: "Ospitalità", es: "Hospitalidad", de: "Gastfreundschaft", pt: "Hospitalidade" }[locale] || "Hospitalité";
  const bergerieLabel = { fr: "Bergerie Pèlerine", en: "Pilgrim Sheepfold", it: "Ovile Pellegrino", es: "Aprisco Peregrino", de: "Pilger-Schäferei", pt: "Aprisco Peregrino" }[locale] || "Bergerie Pèlerine";
  const logisLabel = { fr: "Logis Prieural", en: "Priory Lodging", it: "Alloggio Priorale", es: "Alojamiento Prioral", de: "Prioratswohnung", pt: "Alojamento Prioral" }[locale] || "Logis Prieural";

  const standardLinks = [
    { to: "/galerie", label: t.ui.gallery },
    {
      group: "memoire",
      label: isFr ? "Mémoire des lieux" : "Memory of the place",
      children: [
        { to: "/histoire", label: isFr ? "Histoire" : "History" },
        { to: "/documents", label: t.ui.resources },
      ],
    },
    { to: "/association", label: t.ui.friendsOfPriory },
    {
      group: "hospitality",
      label: hospitalityLabel,
      children: [
        { to: "/dormir-baie-mont-saint-michel", label: isFr ? "Dormir en baie" : "Sleep on the bay" },
        { to: "/bergerie", label: bergerieLabel },
        { to: "/sejour", label: logisLabel },
      ],
    },
    { to: "/offices", label: t.nav.offices },
    { to: "/agenda", label: t.nav.agenda },
    {
      group: "programs",
      label: isFr ? "Programme" : "Programme",
      children: [
        { to: "/fetes-internationales", label: isFr ? "Étapes mondiales 2027" : "2027 worldwide stops" },
        { to: "/concerts", label: t.nav.concerts },
        { to: "/conferences", label: t.nav.conferences },
        { to: "/spectacles", label: isFr ? "Spectacles" : "Performances" },
        { to: "/symposiums", label: isFr ? "Symposiums" : "Symposiums" },
        { to: "/stages", label: isFr ? "Stages & Résidences" : "Workshops & Residencies" },
        { to: "/traversee-de-la-baie", label: isFr ? "Traversée de la baie" : "Bay Crossing" },
      ],
    },
    { to: "/viva-artist", label: "Viva Artist" },
    { to: "/acces", label: accessLabel },
    { to: "/contact", label: t.nav.contact },
  ];

  const presenceLinks = [
    { to: "/presence/origine", label: t.ui.origin },
    { to: "/presence/programme", label: t.ui.programme },
    { to: "/presence/artistes", label: t.ui.artists },
    { to: "/presence/passeurs", label: t.ui.passeurs },
    { to: "/presence/passeurs-foi", label: t.ui.passeursFoi },
    { to: "/presence/passeurs-histoire-sciences", label: t.ui.passeursHistoireSciences },
    { to: "/presence/rencontres-jeunes", label: t.ui.youthMeetings },
    { to: volunteerPath, label: t.ui.becomeVolunteer, accent: true },
    { external: "/documents/livret-fetes-saint-leonard.pdf", label: t.ui.bookletFeasts },
    { to: "/acces", label: accessLabel },
    { to: "/presence/liens-utiles", label: t.ui.usefulLinks },
    { to: "/presence/contacts", label: t.ui.contacts },
  ];

  const links = isPresence ? presenceLinks : standardLinks;

  // Auto-expand the Hospitalité group when the user is on /bergerie or /sejour
  useEffect(() => {
    if (location.pathname.startsWith("/bergerie") || location.pathname.startsWith("/sejour")) {
      setExpandedGroup("hospitality");
    } else if (
      location.pathname.startsWith("/symposiums")
      || location.pathname.startsWith("/stages")
      || location.pathname.startsWith("/traversee")
    ) {
      setExpandedGroup("programs");
    }
  }, [location.pathname]);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="absolute top-0 left-0 right-0 z-40"
        style={{
          background:
            "linear-gradient(to bottom, rgba(20,16,12,0.30) 0%, rgba(20,16,12,0.10) 60%, transparent 100%)",
        }}
      >
        <div className="container-prieure flex items-center justify-between py-6 sm:py-14">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-3"
              aria-label={locale === "fr" ? "Accueil" : "Home"}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: "50%",
                padding: "4px",
                boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,255,255,0.6)",
              }}
            >
              <img
                src="/logo-prieure.png"
                alt="Prieuré saint Léonard de Vains"
                width="48"
                height="48"
                style={{ objectFit: "contain", display: "block" }}
              />
            </div>
            <div className="flex flex-col leading-tight"
                 data-testid="header-site-name"
                 style={{
                   color: "white",
                   textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                 }}>
              <span className="font-display text-[1.05rem] sm:text-[2.35rem]"
                    style={{
                      letterSpacing: "0.06em",
                      lineHeight: 1.0,
                      whiteSpace: "nowrap",
                    }}>
                Prieuré saint Léonard
              </span>
              <span className="font-caps text-[0.5rem] sm:text-[0.62rem] mt-1 sm:mt-1.5 hidden sm:block"
                    style={{
                      letterSpacing: "0.22em",
                      color: "rgba(255,255,255,0.82)",
                      whiteSpace: "nowrap",
                    }}>
                Vains&nbsp;—&nbsp;Baie du Mont Saint Michel
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-4 sm:gap-5">
            {isPresence && (
              <Link
                to="/presence/pre-inscription"
                data-testid="header-presence-prereg-cta"
                className="inline-flex btn btn-primary"
                style={{
                  fontSize: "0.6rem",
                  padding: "0.4rem 0.7rem",
                  letterSpacing: "0.1em",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                  whiteSpace: "nowrap",
                }}
              >
                {t.ui.preRegistration}
              </Link>
            )}
            <LanguageSwitcher />

            {/* Dedicated, always-visible Sign-in button — sits OUTSIDE the burger menu.
                Single login form at /connexion (unified for members + admins). */}
            {member ? (
              <Link
                to="/espace"
                data-testid="header-mySpace-btn"
                className="font-caps text-[0.55rem] sm:text-[0.6rem] px-2.5 sm:px-3 py-1.5 sm:py-2 transition-opacity hover:opacity-80"
                style={{
                  letterSpacing: "0.2em",
                  background: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.55)",
                  color: "white",
                  backdropFilter: "blur(6px)",
                  whiteSpace: "nowrap",
                }}
              >
                {t.ui.mySpace}
              </Link>
            ) : (
              <Link
                to="/connexion"
                data-testid="header-signin-btn"
                className="font-caps text-[0.55rem] sm:text-[0.6rem] px-2.5 sm:px-3 py-1.5 sm:py-2 transition-opacity hover:opacity-80"
                style={{
                  letterSpacing: "0.2em",
                  background: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.55)",
                  color: "white",
                  backdropFilter: "blur(6px)",
                  whiteSpace: "nowrap",
                }}
              >
                {t.ui.signIn}
              </Link>
            )}

            <button
              type="button"
              data-testid="menu-toggle"
              onClick={() => setOpen(true)}
              aria-label={t.ui.openMenu}
              className="flex flex-col items-end justify-center gap-[5px] p-1.5 transition-opacity hover:opacity-80"
              style={{ background: "transparent", border: "none", cursor: "pointer" }}
            >
              <span style={{ display: "block", width: 26, height: 1.5, background: "white", boxShadow: "0 1px 2px rgba(0,0,0,0.4)" }} />
              <span style={{ display: "block", width: 20, height: 1.5, background: "white", boxShadow: "0 1px 2px rgba(0,0,0,0.4)" }} />
              <span style={{ display: "block", width: 26, height: 1.5, background: "white", boxShadow: "0 1px 2px rgba(0,0,0,0.4)" }} />
            </button>
          </div>
        </div>
      </header>

      {/* DRAWER OVERLAY + PANEL (Audace & Perspectives style) */}
      <div
        data-testid="menu-overlay"
        aria-hidden={!open}
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(20,16,12,0.28)",
          backdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 350ms ease",
          zIndex: 60,
        }}
      />
      <aside
        data-testid="menu-drawer"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(360px, 88vw)",
          background: "rgba(245,240,228,0.18)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
          borderLeft: "1px solid rgba(255,255,255,0.22)",
          boxShadow: "-30px 0 80px rgba(0,0,0,0.12)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          zIndex: 70,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Close */}
        <div className="flex justify-end p-6">
          <button
            type="button"
            data-testid="menu-close"
            onClick={() => setOpen(false)}
            aria-label={t.ui.close}
            className="p-2 transition-opacity hover:opacity-70"
            style={{ background: "transparent", border: "none", color: "var(--ink)", cursor: "pointer" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
              <path d="M6 6 L18 18 M18 6 L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Member auth block */}
        <div className="px-8 pb-5" data-testid="drawer-auth-block">
          {member ? (
            <div className="bg-white/60 border p-4" style={{ borderColor: "rgba(20,16,12,0.12)" }}>
              <div className="font-caps text-[0.6rem] mb-1" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
                {(member.role || "").toUpperCase()}
              </div>
              <div className="font-display text-lg">{member.name}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Link to="/espace" onClick={() => setOpen(false)} data-testid="drawer-dashboard" className="btn btn-primary" style={{ fontSize: "0.65rem", padding: "0.45rem 0.8rem" }}>
                  {t.ui.mySpace}
                </Link>
                <button onClick={() => { logout(); setOpen(false); }} data-testid="drawer-logout" className="btn btn-ghost" style={{ fontSize: "0.65rem" }}>
                  {t.ui.logout}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Items */}
        <nav className="flex-1 overflow-y-auto px-8 pb-8">
          {links.map((l, idx) => {
            // Rotating illuminated-manuscript palette — brings joy to the menu.
            const hue = MENU_HUES[idx % MENU_HUES.length];
            if (l.group && Array.isArray(l.children)) {
              return (
                <MenuGroup
                  key={`group-${l.group}`}
                  group={l}
                  hue={hue}
                  isExpanded={expandedGroup === l.group}
                  onToggle={() => setExpandedGroup(expandedGroup === l.group ? null : l.group)}
                  onChildClick={() => setOpen(false)}
                />
              );
            }
            return (
              <MenuLink
                key={l.to || l.hash || l.external}
                link={l}
                hue={hue}
                onNavigate={() => setOpen(false)}
              />
            );
          })}
        </nav>
      </aside>
    </>
  );
}

// ============================== Sub-components ==============================
// Illuminated-manuscript jewel palette — each menu item gets its own hue on
// hover/active, so the whole menu feels alive without losing the medieval
// register. Colours are directly borrowed from Norman & Carolingian manuscripts:
// gold leaf, cinabre, ultramarine (lapis-lazuli), verdigris (copper green),
// madder-red, plum. They stay saturated enough to sing on the parchment
// background but readable when placed on top of the frosted-glass drawer.
const MENU_HUES = [
  "#d4af37", // gold leaf (or)
  "#7b1f2b", // cinabre / madder deep
  "#2b4c8f", // ultramarine (lapis)
  "#3f7a5e", // verdigris
  "#8e5a83", // plum
  "#c26b3a", // saffron
];
const MENU_ITEM_STYLE = { borderBottom: "1px solid rgba(20,16,12,0.07)" };
const MENU_LINK_BASE_CLS = "group block py-4 transition-colors";


function _linkTestid(link) {
  if (link.to) return link.to === "/" ? "home" : link.to.slice(1);
  if (link.hash) return link.hash.replace("#", "");
  return "livret";
}


function MenuLink({ link, hue = "#7b1f2b", onNavigate }) {
  const testid = _linkTestid(link);
  const inner = (
    <span
      className="font-display text-2xl tracking-tight transition-colors"
      style={{ transition: "color 220ms ease" }}
    >
      {link.label}
    </span>
  );

  // Idle vs hover/active: idle stays dark ink for legibility, hover/active
  // reveals the item's own jewel hue (per-item, from MENU_HUES rotation).
  const idleColor = "var(--ink)";

  const applyHue = (e) => { e.currentTarget.style.color = hue; };
  const resetHue = (e) => { e.currentTarget.style.color = idleColor; };

  if (link.to) {
    return (
      <NavLink
        to={link.to}
        end={link.end}
        onClick={onNavigate}
        data-testid={`menu-nav-${testid}`}
        onMouseEnter={applyHue}
        onMouseLeave={resetHue}
        onFocus={applyHue}
        onBlur={resetHue}
        className={MENU_LINK_BASE_CLS}
        style={({ isActive }) => ({
          ...MENU_ITEM_STYLE,
          color: isActive ? hue : idleColor,
        })}
      >
        {inner}
      </NavLink>
    );
  }

  if (link.external) {
    return (
      <a
        href={link.external}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`menu-nav-${testid}`}
        onClick={onNavigate}
        onMouseEnter={applyHue}
        onMouseLeave={resetHue}
        onFocus={applyHue}
        onBlur={resetHue}
        className={MENU_LINK_BASE_CLS}
        style={{ ...MENU_ITEM_STYLE, color: idleColor }}
      >
        {inner}
      </a>
    );
  }

  // Hash anchor — scroll to in-page section
  return (
    <a
      href={link.hash}
      data-testid={`menu-nav-${testid}`}
      onClick={(e) => {
        e.preventDefault();
        const el = document.getElementById(link.hash.slice(1));
        if (el) {
          const rect = el.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top - 20, behavior: "smooth" });
        }
        onNavigate();
      }}
      onMouseEnter={applyHue}
      onMouseLeave={resetHue}
      className={MENU_LINK_BASE_CLS}
      style={{ ...MENU_ITEM_STYLE, color: idleColor }}
    >
      {inner}
    </a>
  );
}


function MenuGroup({ group, hue = "#d4af37", isExpanded, onToggle, onChildClick }) {
  return (
    <div data-testid={`menu-group-${group.group}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`menu-group-toggle-${group.group}`}
        aria-expanded={isExpanded}
        onMouseEnter={(e) => { e.currentTarget.style.color = hue; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--ink)"; }}
        className="w-full flex items-center justify-between py-4 transition-colors"
        style={{
          ...MENU_ITEM_STYLE,
          background: "transparent",
          color: isExpanded ? hue : "var(--ink)",
          transition: "color 220ms ease",
        }}
      >
        <span className="font-display text-2xl tracking-tight">{group.label}</span>
        <span
          className="font-display text-xl ml-3 transition-transform"
          style={{
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            color: hue,
          }}
          aria-hidden
        >
          ›
        </span>
      </button>
      {isExpanded && (
        <div
          data-testid={`menu-group-children-${group.group}`}
          style={{
            ...MENU_ITEM_STYLE,
            background: "rgba(255,255,255,0.18)",
          }}
        >
          {group.children.map((c) => {
            const childTestid = c.to ? c.to.slice(1).replace(/\//g, "-") : "child";
            return (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={onChildClick}
                data-testid={`menu-nav-${childTestid}`}
                onMouseEnter={(e) => { e.currentTarget.style.color = hue; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--ink-2)"; }}
                className="block py-3 pl-6 pr-2 transition-colors font-display text-lg"
                style={({ isActive }) => ({
                  color: isActive ? hue : "var(--ink-2)",
                })}
              >
                <span style={{ color: hue, marginRight: 8 }}>✦</span>
                {c.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
